import { collection, query, where, getDocs, getDoc, doc, runTransaction } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface OrderItem {
  productId: string;
  quantity: number;
}

interface Order {
  id: string;
  status: string;
  items: OrderItem[];
  stockProcessed?: boolean;
}

async function updateProductStock(
  productId: string,
  quantity: number,
  isReturn: boolean
): Promise<void> {
  const productRef = doc(db, 'products', productId);

  await runTransaction(db, async (transaction) => {
    const productDoc = await transaction.get(productRef);

    if (!productDoc.exists()) {
      throw new Error(`Produto ${productId} não encontrado`);
    }

    const currentStock = productDoc.data()?.stockQuantity || 0;
    let newStock: number;

    if (isReturn) {
      newStock = currentStock + quantity;
    } else {
      newStock = currentStock - quantity;
      
      if (newStock < 0) {
        throw new Error(`Stock insuficiente para o produto ${productId}`);
      }
    }

    // Atualizar ambos os campos de stock para manter consistência
    transaction.update(productRef, {
      stock: newStock,
      stockQuantity: newStock,
      stockStatus: newStock === 0 ? 'outofstock' : 'instock',
      updatedAt: new Date().toISOString()
    });
  });
}

async function processOrderStock(order: Order) {
  try {
    for (const item of order.items) {
      await updateProductStock(item.productId, item.quantity, false);
    }

    await runTransaction(db, async (transaction) => {
      const orderRef = doc(db, 'orders', order.id);
      transaction.update(orderRef, {
        stockProcessed: true,
        stockProcessedAt: new Date().toISOString()
      });
    });
  } catch (error) {
    console.error(`[Stock Update] Error processing order ${order.id}:`, error);
    throw error;
  }
}

async function restoreOrderStock(order: Order) {
  try {
    for (const item of order.items) {
      await updateProductStock(item.productId, item.quantity, true);
    }

    await runTransaction(db, async (transaction) => {
      const orderRef = doc(db, 'orders', order.id);
      transaction.update(orderRef, {
        stockProcessed: true,
        stockProcessedAt: new Date().toISOString()
      });
    });
  } catch (error) {
    console.error(`[Stock Update] Error restoring order ${order.id}:`, error);
    throw error;
  }
}

export async function checkAndUpdateOrdersStock() {
  try {
    const ordersRef = collection(db, 'orders');
    const ordersToProcess = query(
      ordersRef,
      where('stockProcessed', '==', false),
      where('status', 'in', ['pending', 'preparation'])
    );

    const ordersSnapshot = await getDocs(ordersToProcess);
    const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));

    const cancelledOrders = query(
      ordersRef,
      where('stockProcessed', '==', false),
      where('status', '==', 'cancelled')
    );

    const cancelledSnapshot = await getDocs(cancelledOrders);
    const cancelledOrdersData = cancelledSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));

    console.log(`[Stock Update] Processing ${orders.length} orders and ${cancelledOrdersData.length} cancelled orders`);

    for (const order of orders) {
      try {
        await processOrderStock(order);
        console.log(`[Stock Update] Successfully processed order ${order.id}`);
      } catch (error) {
        console.error(`[Stock Update] Failed to process order ${order.id}:`, error);
      }
    }

    for (const order of cancelledOrdersData) {
      try {
        await restoreOrderStock(order);
        console.log(`[Stock Update] Successfully restored stock for cancelled order ${order.id}`);
      } catch (error) {
        console.error(`[Stock Update] Failed to restore stock for order ${order.id}:`, error);
      }
    }
  } catch (error) {
    console.error('[Stock Update] Error in checkAndUpdateOrdersStock:', error);
    throw error;
  }
}
